import chunk from 'lodash/chunk';
import qs from 'qs';

import { PublicOfferV2 } from '@luxuryescapes/contract-public-offer';
import { components, operations, paths } from '@luxuryescapes/contract-public-offer/server';

import { json_headers, request, rethrowAbortError, throwIfAborted } from './common';

const PUBLIC_OFFER_LOOKUP_CHUNK_SIZE = 100;

export type PublicOffer =
  | PublicOfferV2.LeOffer
  | components['schemas']['leHotelOffer']
  | components['schemas']['v1TourOffer']
  | components['schemas']['v2TourOffer']
  | components['schemas']['bedbankOffer'];

type PublicOfferFields = {
  hotelName?: string;
  ids: Array<string>;
  offerName?: string;
  offerSlug?: string;
};

type OfferRecord = {
  id?: string;
  id_salesforce_external?: string;
  name?: string;
  property?: {
    name?: string;
  };
  slug?: string;
  title?: string;
};

export function readPublicOfferFields(offer: unknown): PublicOfferFields {
  const record = offer as OfferRecord | null | undefined;

  return {
    hotelName: record?.property?.name?.trim() || undefined,
    ids: [record?.id, record?.id_salesforce_external].filter((id): id is string => Boolean(id)),
    offerName: (record?.name ?? record?.title)?.trim() || undefined,
    offerSlug: record?.slug?.trim() || undefined,
  };
}

const convertAccommodationPackages = (
  publicOffer: PublicOfferV2.LeHotelOffer,
): Array<Omit<App.AccommodationPackage, 'addons'>> => {
  return publicOffer.options.map((option) => {
    const ratePlan = publicOffer.ratePlans[option.fkRatePlanId];
    const publicOfferRoomRate = publicOffer.roomRates[option.fkRoomRateId];
    const roomType = publicOffer.roomTypes[option.fkRoomTypeId];

    const roomRate = {
      id: publicOfferRoomRate.id,
      capacities: publicOfferRoomRate.capacities,
      rate_plan: { id: option.fkRatePlanId, name: ratePlan.id },
    };

    return {
      le_package_id: option.fkPackageId, // todo: fix below to use sfid once offer changes published
      id_salesforce_external: option.fkPackageId,
      fk_property_id: publicOffer.property.id,
      fk_room_type_id: option.fkRoomTypeId,
      fk_room_rate_id: option.fkRoomRateId,
      name: option.name,
      price: option.price,
      number_of_nights: option.duration,
      offer_id_salesforce_external: publicOffer.id,
      package_options: [],
      prices: [
        {
          currency_code: option.currencyCode,
          price: option.price,
        },
      ],
      bundle_prices: [],
      regions: [],
      room_rate: roomRate,
      room_rates: [roomRate],
      room_type: {
        id: option.fkRoomTypeId,
        name: roomType.name,
        room_rates: [roomRate],
      },
      status: 'content-approved',
      unique_key: option.id,
      description: option.name,
      cost_price: null,
      target_upgrade_package_id: '',
      package_target_upgrades: [],
    };
  });
};

const convertTourPackages = (publicOffer: PublicOfferV2.LeTourOffer): Array<Omit<App.TourPackage, 'addons'>> => {
  return publicOffer.options.map((option) => {
    const pkg = publicOffer.packages[option.fkPackageId];
    return {
      id_salesforce_external: option.fkPackageId,
      name: pkg.name,
      price: option.price,
      number_of_days: option.duration,
      prices: [
        {
          currency_code: option.currencyCode,
          price: option.price,
        },
      ],
      bundle_prices: [],
      offer_id_salesforce_external: publicOffer.id,
      unique_key: option.id,
      fk_tour_id: publicOffer.tour.id,
      fk_tour_option_id: option.id,
      status: 'content-approved',
      regions: [],
      description: pkg.name,
      cost_price: null,
      target_upgrade_package_id: '',
    };
  });
};

const convertTourOffer = (publicOffer: PublicOfferV2.LeTourOffer): App.TourOffer => {
  return {
    brand_schedules: [],
    id_salesforce_external: publicOffer.id,
    images: publicOffer.images as unknown as Array<App.OfferImage>,
    location: publicOffer.location,
    locations: publicOffer.tags.location,
    name: publicOffer.name,
    packages: convertTourPackages(publicOffer),
    slug: publicOffer.slug,
    type: publicOffer.type,
    book_by_date: '',
    travel_to_date: '',
    enable_customer_portal_date_change: false,
    days_before_check_in_changes_disallowed: 21,
    number_of_date_changes: '0',
    package_upgrades_allowed: false,
    experiences_in_flow: false,
    experiences_curated: false,
    subjectLine: publicOffer.subjectLine,
    onlinePurchaseSchedule: publicOffer.schedules.onlinePurchase,
    listSchedule: publicOffer?.schedules?.listVisibility,
  };
};

const convertOffer = (publicOffer: PublicOfferV2.LeOffer): App.Offer => {
  if (publicOffer.type === 'tour') {
    return convertTourOffer(publicOffer);
  }

  return {
    brand_schedules: [],
    id_salesforce_external: publicOffer.id,
    images: publicOffer.images as unknown as Array<App.OfferImage>,
    location: publicOffer.location,
    locations: publicOffer.tags.location,
    name: publicOffer.name,
    packages: convertAccommodationPackages(publicOffer),
    slug: publicOffer.slug,
    type: publicOffer.type,
    book_by_date: '',
    travel_to_date: '',
    enable_customer_portal_date_change: false,
    days_before_check_in_changes_disallowed: 21,
    number_of_date_changes: '0',
    package_upgrades_allowed: false,
    experiences_in_flow: false,
    experiences_curated: false,
    property: publicOffer.property,
    subjectLine: publicOffer.subjectLine,
    onlinePurchaseSchedule: publicOffer.schedules.onlinePurchase,
    listSchedule: publicOffer?.schedules?.listVisibility,
    luxPlusSchedule: publicOffer?.schedules?.luxPlus,
  };
};

export default class PublicOffersService {
  static getPublicOfferById(
    id_salesforce_external: string,
    regionCode: string,
    brand: App.Tenant['brand'],
    options?: { signal?: AbortSignal },
  ): Promise<{
    result:
      | components['schemas']['leHotelOffer']
      | components['schemas']['v1TourOffer']
      | components['schemas']['v2TourOffer']
      | components['schemas']['bedbankOffer'];
  }> {
    return request(
      `${window.configs.API_HOST}/api/v2/public-offers/${id_salesforce_external}?region=${regionCode}&brand=${brand}`,
      { method: 'GET', credentials: 'omit', signal: options?.signal },
    );
  }

  static getTourV2OfferById(
    id: string,
    regionCode: string,
    brand: App.Tenant['brand'],
  ): Promise<{ result: PublicOfferV2.TourV2Offer }> {
    return request(`${window.configs.API_HOST}/api/v2/public-offers/${id}?region=${regionCode}&brand=${brand}`, {
      method: 'GET',
      credentials: 'omit',
    });
  }
  static getBedbankOfferById(
    id: string,
    regionCode: string,
    brand: App.Tenant['brand'],
    options?: { signal?: AbortSignal },
  ): Promise<{ result: PublicOfferV2.BedbankOffer }> {
    return request(`${window.configs.API_HOST}/api/v2/public-offers/${id}?region=${regionCode}&brand=${brand}`, {
      method: 'GET',
      credentials: 'omit',
      signal: options?.signal,
    });
  }

  static getMultiplePublicOffersById(
    queryParams: operations['offerIndex']['parameters']['query'],
    options?: { signal?: AbortSignal },
  ): Promise<{ result: Array<PublicOfferV2.LeOffer> }> {
    return request(`${window.configs.API_HOST}/api/v2/public-offers?${qs.stringify(queryParams)}`, {
      method: 'GET',
      credentials: 'omit',
      signal: options?.signal,
    });
  }

  static async getPublicOffersByIds(
    ids: Array<string>,
    regionCode: string,
    brand: App.Tenant['brand'],
    options?: { signal?: AbortSignal; skipIndividualFallback?: boolean },
  ): Promise<Map<string, PublicOffer>> {
    const signal = options?.signal;
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    const offersById = new Map<string, PublicOffer>();

    throwIfAborted(signal);

    if (uniqueIds.length === 0) {
      return offersById;
    }

    await Promise.all(
      chunk(uniqueIds, PUBLIC_OFFER_LOOKUP_CHUNK_SIZE).map(async (offerIds) => {
        try {
          const offers = await PublicOffersService.getMultiplePublicOffersById(
            {
              offerIds: offerIds.join(','),
              region: regionCode as operations['offerIndex']['parameters']['query']['region'],
              brand: brand as operations['offerIndex']['parameters']['query']['brand'],
            },
            { signal },
          );

          offers.result.forEach((offer) => {
            readPublicOfferFields(offer).ids.forEach((id) => offersById.set(id, offer));
          });
        } catch (error) {
          rethrowAbortError(error, signal);
        }
      }),
    );

    if (options?.skipIndividualFallback) {
      return offersById;
    }

    const unresolvedIds = uniqueIds.filter((id) => !offersById.has(id));

    await Promise.all(
      unresolvedIds.map(async (offerId) => {
        try {
          const offer = await PublicOffersService.getPublicOfferById(offerId, regionCode, brand, { signal });
          const resolvedOffer = offer.result;

          offersById.set(offerId, resolvedOffer);
          readPublicOfferFields(resolvedOffer).ids.forEach((id) => offersById.set(id, resolvedOffer));
        } catch (error) {
          rethrowAbortError(error, signal);
        }
      }),
    );

    return offersById;
  }

  static async getOfferById(
    id_salesforce_external: string,
    regionCode: string,
    brand: App.Tenant['brand'],
  ): Promise<App.Offer> {
    const publicOffer = await PublicOffersService.getPublicOfferById(id_salesforce_external, regionCode, brand);
    if (publicOffer.result.type === 'bedbank_hotel' || publicOffer.result.type === 'tour_v2') {
      throw new Error('Not an LE offer');
    }
    return convertOffer(publicOffer.result);
  }

  static async getMultipleOffersById(
    ids: Array<string>,
    regionCode: operations['offerIndex']['parameters']['query']['region'],
    brand: operations['offerIndex']['parameters']['query']['brand'],
  ): Promise<Array<App.Offer>> {
    const publicOffers = await PublicOffersService.getMultiplePublicOffersById({
      offerIds: ids.toString(),
      region: regionCode,
      brand,
    });

    return publicOffers.result.map((publicOffer) => convertOffer(publicOffer));
  }

  static async resyncData(offerId: string, propertyId: string) {
    return request(`${window.configs.API_HOST}/api/v2/public-offers/${offerId}/manual-resync`, {
      method: 'PATCH',
      body: JSON.stringify({ propertyId }),
      headers: json_headers,
    });
  }

  static async createTranslationRequest(
    payload: paths['/api/v2/public-offers/translations']['post']['requestBody']['content']['application/json'],
    params: {
      brand: string;
      regionCode: string;
    },
  ): Promise<
    | paths['/api/v2/public-offers/translations']['post']['responses']['202']['content']['application/json']
    | paths['/api/v2/public-offers/translations']['post']['responses']['208']['content']['application/json']
  > {
    const url = new URL('/api/v2/public-offers/translations', window.configs.API_HOST);
    url.searchParams.set('brand', params.brand);
    url.searchParams.set('region', params.regionCode);

    return request(url.toString(), {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: json_headers,
    });
  }
}
